import React, { forwardRef, memo, useEffect, useImperativeHandle, useState } from 'react';
import { AuditItem, GetAuditV2Query, GetAuditV2RootPath, QKeys } from 'src/api/types';
import { Poppins, Spacer, TableSkeleton } from 'src/common';
import { useInfiniteQuery } from 'react-query';
import { getAuditV2 } from 'src/api/other';
import _ from 'lodash';
import GenericTable from '../GenericTable';
import { ActionsGrid, ToolBar, Switch } from './comps';
import { composeSentence } from './comps/util';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { useAuditColumns } from './useAuditColumns';
import { AuditHandle } from './types';
import { formatDate } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';

interface AuditProps {
  rootPath: GetAuditV2RootPath;
  initQuery: GetAuditV2Query; //leave as required, otherwise falls into infinite loop
  noHide?: boolean;
  maxHeight?: number | string | null;
  customLabel?: {
    open: string;
    close: string;
  };
  tableId: TableIds;
  topSpacer?: number;
  toolbar?: boolean;
  initialShowAudit?: boolean;
}

const Audit = memo(
  forwardRef<AuditHandle, AuditProps>(
    (
      {
        rootPath,
        initQuery = {},
        noHide,
        maxHeight = 700,
        customLabel,
        tableId,
        topSpacer = 30,
        toolbar,
        initialShowAudit = false,
      },
      ref,
    ) => {
      const [showHistory, setShowHistory] = useState(!!noHide);
      const [desc, setDesc] = useState(true);
      const [sortBy, setSortBy] = useState('day');
      const [items, setItems] = useState<AuditItem[]>([]);
      const [skip, setSkip] = useState(0);
      const [showAudit, setShowAudit] = useState(initialShowAudit);
      const { features } = useAuth();

      const reset = () => {
        setItems([]);
        setSkip(0);
        refetch();
      };

      useImperativeHandle(ref, () => ({
        refetch,
      }));

      const { canFetchMore, isFetching, refetch } = useInfiniteQuery(
        [QKeys.Audit, { rootPath, sortBy, desc, skip, eventOnly: !showAudit, ...initQuery }],
        () =>
          getAuditV2(rootPath, {
            skip,
            orderBy: sortBy,
            dir: desc ? 'desc' : 'asc',
            eventOnly: !showAudit,
            ...initQuery,
          }),
        {
          onSuccess: (data) => {
            if (data.length === 0 || skip === 0) {
              setItems(data.flatMap((el) => el.items));
            } else {
              setItems((prev) => [...prev, ...data.flatMap((el) => el.items)]);
            }
          },
          refetchOnMount: true,
          getFetchMore: (lastGroup, allGroups) => {
            const itemsFetched = allGroups.reduce((acc, group) => acc + group.items.length, 0);
            const moreItemsExist = itemsFetched < lastGroup.total;
            return moreItemsExist ? itemsFetched : false;
          },
        },
      );

      const itemsComposed = items.map((el) => {
        return {
          ...el,
          date_time: formatDate(el.date_time, {
            formatType: 'datetime',
          }),
          action_title: el.action_title || composeSentence(el.actions),
        };
      });

      const { columns, GTColumnSelectAnchorExported } = useAuditColumns({
        data: itemsComposed,
        tableId,
        refetch: reset,
        showAudit,
      });

      useEffect(() => {
        reset();
      }, [initQuery, sortBy, desc, showAudit]);

      return (
        <>
          {!noHide && (
            <Switch
              title={customLabel ? (showHistory ? customLabel.open : customLabel.close) : ''}
              showHistory={showHistory}
              setShowHistory={(val) => {
                setShowHistory(val);
                mpEvent(MPEvents.AuditView, {
                  value: val ? 'open' : 'close',
                });
              }}
            />
          )}
          {showHistory && (
            <>
              {toolbar && features['app.events'] && (
                <ToolBar showAudit={showAudit} setShowAudit={(e) => setShowAudit(e)} />
              )}
              {isFetching && !items.length ? (
                <>
                  <Spacer $px={topSpacer} />
                  <TableSkeleton />
                </>
              ) : (
                <>
                  {!items?.length ? (
                    <>
                      <Spacer $px={topSpacer} />
                      <Poppins px={14} color="cflowerBlue">
                        No activity recorded yet
                      </Poppins>
                    </>
                  ) : (
                    <>
                      <Spacer $px={topSpacer} />
                      <GenericTable
                        tableId={tableId}
                        data={itemsComposed}
                        columns={columns}
                        GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
                        expandContent={(data) =>
                          data.actions?.length ? <ActionsGrid actions={data.actions || []} /> : undefined
                        }
                        controledSorting={{
                          state: { id: sortBy, desc },
                          onChange: ({ id, desc }) => {
                            setSortBy(id);
                            setDesc(desc);
                          },
                        }}
                        onBottomReached={() => {
                          if (canFetchMore && !isFetching) {
                            setSkip(items.length + 1);
                          }
                        }}
                        layout={{
                          maxHeight,
                          stickyHeader: true,
                        }}
                        usePagination={null}
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      );
    },
  ),
  (prev, next) => _.isEqual(prev, next),
);

export default Audit;
